import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Image as ImageIcon } from "lucide-react";

const COLORS = ["#2563EB","#0EA5E9","#7C3AED","#10B981","#F59E0B","#EF4444","#EC4899","#0F172A"];

export default function CompanySettings() {
  const { company, refreshMe } = useAuth();
  const [form, setForm] = useState({ logo_url: "", primary_color: "#2563EB", welcome_message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company) setForm({ logo_url: company.logo_url || "", primary_color: company.primary_color || "#2563EB", welcome_message: company.welcome_message || "" });
  }, [company]);

  const save = async () => {
    setSaving(true);
    try { await api.patch("/company/branding", form); toast.success("Branding updated"); refreshMe(); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  if (!company) return null;

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-10">
      <div className="label-caps mb-2">Admin</div>
      <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900">Company Settings</h1>
      <p className="text-slate-500 mt-3 mb-8">Make PeopleIQ feel like your company's platform.</p>

      <div className="card space-y-6">
        <div>
          <div className="label-caps mb-4">Workspace</div>
          <div className="grid grid-cols-2 gap-4">
            <div><div className="text-xs text-slate-500 mb-1">Company</div><div className="text-slate-900 font-medium">{company.name}</div></div>
            <div><div className="text-xs text-slate-500 mb-1">Workspace URL</div><div className="mono text-sm text-slate-900">{company.slug}.peopleiq.org</div></div>
            <div><div className="text-xs text-slate-500 mb-1">Industry</div><div className="text-slate-900">{company.industry || "—"}</div></div>
            <div><div className="text-xs text-slate-500 mb-1">Country</div><div className="text-slate-900">{company.country || "—"}</div></div>
          </div>
        </div>

        <div className="divider-h" />

        <div>
          <label className="label-caps block mb-3">Brand color</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {COLORS.map(c => (
              <button key={c} onClick={() => setForm({...form, primary_color: c})} data-testid={`color-${c}`}
                className={`w-9 h-9 rounded-lg border-2 transition-all ${form.primary_color === c ? "border-slate-900 scale-110" : "border-transparent"}`}
                style={{ background: c }} />
            ))}
          </div>
          <input value={form.primary_color} onChange={(e) => setForm({...form, primary_color: e.target.value})} className="input-base mono" placeholder="#2563EB" />
        </div>

        <div>
          <label className="label-caps block mb-2 flex items-center gap-2"><ImageIcon size={12} /> Logo URL</label>
          <input data-testid="logo-url" value={form.logo_url} onChange={(e) => setForm({...form, logo_url: e.target.value})} className="input-base mono" placeholder="https://your-cdn/logo.png" />
          <div className="text-xs text-slate-500 mt-2">Square PNG works best. Leave blank to use initials.</div>
        </div>

        <div>
          <label className="label-caps block mb-2">Welcome message</label>
          <textarea data-testid="welcome-msg" value={form.welcome_message} onChange={(e) => setForm({...form, welcome_message: e.target.value})} className="input-base" rows={3} />
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="save-branding" className="btn-primary"><Save size={14} /> {saving ? "Saving…" : "Save branding"}</button>
        </div>
      </div>
    </div>
  );
}
